import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lightbulb, BarChart3, Target, AlertTriangle, ArrowRight, ChevronRight, Crown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { parseJsonArray } from '../utils/parseJsonArray';
import './TradingStrategy.css';

const TradingStrategy = () => {
  const navigate = useNavigate();
  const { isVip, refreshUser, isAuthenticated } = useAuth();
  const [strategy, setStrategy] = useState(null);
  const [isVipContent, setIsVipContent] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadStrategy = async () => {
      // Refresh user data first to get latest VIP status
      if (isAuthenticated) {
        await refreshUser();
      }
      // Then fetch strategy
      await fetchTodayStrategy();
    };
    loadStrategy();
  }, [isAuthenticated]);

  const fetchTodayStrategy = async () => {
    try {
      setLoading(true);
      const response = await api.get('/strategies/today');
      if (response.data.success && response.data.data.strategy) {
        const s = response.data.data.strategy;
        const focusAreas = parseJsonArray(s.focusAreas);
        
        setStrategy({
          ...s,
          focusAreas,
          updatedAt: new Date(s.publishedAt || s.updatedAt).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
            timeZone: 'America/New_York',
            timeZoneName: 'short'
          })
        });
        // Use the isVipContent from server response, which is based on fresh user role check
        setIsVipContent(response.data.data.isVipContent || false);
      }
    } catch (error) {
      console.error('Failed to fetch strategy:', error);
    } finally {
      setLoading(false);
    }
  };

  // Default data if no strategy available
  const defaultStrategy = {
    updatedAt: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
    marketAnalysis: 'Market analysis will be updated soon. Please check back later for the latest trading insights.',
    focusAreas: ['Check back for today\'s focus areas'],
    riskWarning: 'Always manage your risk properly. Past performance is not indicative of future results.',
    riskLevel: 5
  };

  const data = strategy || defaultStrategy;
  const riskPercentage = ((data.riskLevel || 5) / 10) * 100;

  if (loading) {
    return (
      <div className="trading-strategy trading-strategy--loading">
        <div className="trading-strategy__skeleton"></div>
      </div>
    );
  }

  return (
    <div className="trading-strategy">
      {/* Section Header */}
      <div className="trading-strategy__header">
        <div className="trading-strategy__title-wrapper">
          <div className={`trading-strategy__icon ${isVipContent ? 'trading-strategy__icon--vip' : ''}`}>
            {isVipContent ? <Crown size={24} /> : <Lightbulb size={24} />}
          </div>
          <div>
            <div className="trading-strategy__title-row">
              <h2 className="trading-strategy__title">{data.title || "Daily Market Strategy - Tech Focus"}</h2>
              {isVipContent && (
                <span className="trading-strategy__vip-exclusive">
                  <Crown size={14} />
                  VIP Exclusive
                </span>
              )}
            </div>
            <p className="trading-strategy__updated">Updated: {data.updatedAt}</p>
          </div>
        </div>
        {!isVipContent && (
          <button className="trading-strategy__vip-btn" onClick={() => navigate('/vip')}>
            <span className="trading-strategy__vip-badge">VIP</span>
            Upgrade for Premium
            <ArrowRight size={16} />
          </button>
        )}
      </div>

      {/* Content Grid */}
      <div className="trading-strategy__grid">
        {/* Market Analysis */}
        <div className="trading-strategy__card trading-strategy__card--analysis">
          <div className="trading-strategy__card-header">
            <h3 className="trading-strategy__card-title">
              <BarChart3 size={20} className="trading-strategy__card-icon" />
              Market Analysis
            </h3>
          </div>
          <p className="trading-strategy__card-content">
            {data.marketAnalysis}
          </p>
        </div>

        {/* Focus Areas */}
        <div className="trading-strategy__card trading-strategy__card--focus">
          <div className="trading-strategy__card-header">
            <h3 className="trading-strategy__card-title">
              <Target size={20} className="trading-strategy__card-icon" />
              Focus Areas
            </h3>
            {isVipContent && <span className="trading-strategy__alert-badge">🔔 VIP Alert 🔔</span>}
          </div>
          <ul className="trading-strategy__focus-list">
            {data.focusAreas.map((area, index) => (
              <li key={index} className="trading-strategy__focus-item trading-strategy__focus-item--highlight">
                <span className="trading-strategy__focus-arrow">▶</span>
                <span className="trading-strategy__focus-text">{area}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Risk Warning */}
        <div className="trading-strategy__card trading-strategy__card--risk">
          <div className="trading-strategy__card-header">
            <h3 className="trading-strategy__card-title trading-strategy__card-title--warning">
              <AlertTriangle size={20} className="trading-strategy__card-icon" />
              Risk Warning
            </h3>
          </div>
          <p className="trading-strategy__card-content">
            {data.riskWarning}
          </p>
          <div className="trading-strategy__risk-indicator">
            <div className="trading-strategy__risk-bar">
              <div className="trading-strategy__risk-fill" style={{ width: `${riskPercentage}%` }}></div>
            </div>
            <span className="trading-strategy__risk-label">
              Risk Level: {riskPercentage <= 30 ? 'Low' : riskPercentage <= 60 ? 'Moderate' : 'High'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TradingStrategy;
